import { useQuery } from '@apollo/client';
import type { UseDirectDealsListFetch } from '@openx/components-direct-deals/lib/utils/context/types';
import type { DirectDealsListItem } from '@openx/types/directDeals';
import { useMemo } from 'react';
import { TABLE_PAGE_SIZE } from '../../../config';
import { GetPublisherProfilesListDocument } from '../../../graphql/query/directDeals/GetPublisherProfilesList.generated';
import { getQueryListFilter } from './getQueryListFilter';
import { getQueryListSort } from './getQueryListSort';

export const useDirectDealsListFetch: UseDirectDealsListFetch = ({ criteria, skip }) => {
  const filter = useMemo(() => getQueryListFilter(criteria), [criteria]);
  const sort = useMemo(() => getQueryListSort(criteria.sort), [criteria]);

  const {
    data: { publisherProfile } = {},
    loading,
    error,
  } = useQuery(GetPublisherProfilesListDocument, {
    variables: {
      limit: 0, // we need to set limit to 0 to get all data at once (MVP)
      offset: 0,
      filter,
      sort,
    },
    skip,
  });

  const mappedProfiles: DirectDealsListItem[] = useMemo(
    () =>
      (publisherProfile || []).map(({ obj }) => ({
        id: obj.id,
        name: obj.name,
        headline: obj.headline,
        description: obj.description,
        logo: obj.logo,
        audience_distribution_female:
          obj.audience_distribution_female !== null ? Number(obj.audience_distribution_female) : null,
        audience_distribution_male:
          obj.audience_distribution_male !== null ? Number(obj.audience_distribution_male) : null,
        audience_distribution_other:
          obj.audience_distribution_other !== null ? Number(obj.audience_distribution_other) : null,
        cpm_low: Number(obj.cpm_low),
        cpm_high: Number(obj.cpm_high),
        geo_region: obj.geo_region,
        mln_impressions: Number(obj.mln_impressions),
        mln_unique_visitors: Number(obj.mln_unique_visitors),
        top_countries: obj.top_countries,
        formats: obj.formats,
        devices: obj.devices,
        ctv_type: obj.ctv_type,
        channels: obj.channels,
        deal_types: obj.deal_types,
        sensitive_categories: obj.sensitive_categories,
        featured: obj.featured,
        tv_by_openx: obj.tv_by_openx,
      })),
    [publisherProfile]
  );

  const paginatedItems: DirectDealsListItem[] = useMemo(() => {
    if (criteria.pagination) {
      const { pageNumber, pageSize } = criteria.pagination;
      const startIndex = (pageNumber - 1) * pageSize;
      const endIndex = startIndex + pageSize;

      return mappedProfiles.slice(startIndex, endIndex);
    }

    return mappedProfiles;
  }, [mappedProfiles, criteria]);

  return {
    error,
    loading,
    directDealsItems: paginatedItems,
    tableCriteria: {
      ...criteria,
      pagination: {
        ...criteria.pagination,
        pageNumber: criteria.pagination?.pageNumber || 1,
        pageSize: criteria.pagination?.pageSize || TABLE_PAGE_SIZE,
        totalCount: mappedProfiles.length,
      },
    },
  };
};
